import { Controller } from "@hotwired/stimulus"

// Map slider selectors to the Swiper modules they need
const modulesMap = {
  "location-slider-one": ["Navigation"],
  "testimonial-slider-one": ["Navigation", "EffectFade", "Autoplay"],
  "testimonial-slider-two": ["Navigation", "Autoplay"],
  "category-slider-one": ["Navigation"],
  "hospital-slider-two": ["Navigation"],
  "doctor-slider-one": ["Navigation"],
  "brand-slider": ["Autoplay"]
}

// Slider configurations
const sliderConfigs = [
  {
    selector: ".location-slider-one",
    options: {
      loop: true,
      speed: 1000,
      spaceBetween: 24,
      navigation: { nextEl: ".location-next", prevEl: ".location-prev" },
      breakpoints: {
        0: { slidesPerView: 1 },
        768: { slidesPerView: 1.5 },
        992: { slidesPerView: 2.2 },
        1200: { slidesPerView: 2.3 },
        1300: { slidesPerView: 2.5 },
        1400: { slidesPerView: 2.9 },
        1600: { slidesPerView: 3.3, spaceBetween: 30 }
      }
    }
  },
  {
    selector: ".testimonial-slider-one",
    options: {
      loop: false,
      speed: 1000,
      slidesPerView: 1,
      autoplay: { delay: 3500, disableOnInteraction: true },
      navigation: { nextEl: ".testimonial-next", prevEl: ".testimonial-prev" }
    }
  },
  {
    selector: ".testimonial-slider-two",
    options: {
      loop: true,
      speed: 1000,
      slidesPerView: 1,
      spaceBetween: 24,
      autoplay: { delay: 3500, disableOnInteraction: true },
      navigation: { nextEl: ".testimonial-next", prevEl: ".testimonial-prev" },
      breakpoints: {
        0: { slidesPerView: 1 },
        992: { slidesPerView: 1.3 },
        1200: { slidesPerView: 1.6 },
        1400: { slidesPerView: 2 }
      }
    }
  },
  {
    selector: ".category-slider-one",
    options: {
      loop: true,
      speed: 1000,
      slidesPerView: 1,
      spaceBetween: 24,
      navigation: { nextEl: ".category-next", prevEl: ".category-prev" },
      breakpoints: {
        0: { slidesPerView: 1 },
        768: { slidesPerView: 3 },
        1200: { slidesPerView: 4 },
        1400: { slidesPerView: 5, spaceBetween: 23 },
        1600: { slidesPerView: 6, spaceBetween: 28 }
      }
    }
  },
  {
    selector: ".hospital-slider-two",
    options: {
      loop: false,
      speed: 1000,
      slidesPerView: 1,
      spaceBetween: 24,
      navigation: { nextEl: ".hospital-next", prevEl: ".hospital-prev" },
      breakpoints: {
        0: { slidesPerView: 1 },
        768: { slidesPerView: 2 },
        1200: { slidesPerView: 3 },
        1400: { slidesPerView: 3, spaceBetween: 23 },
        1600: { slidesPerView: 4, spaceBetween: 30 }
      }
    }
  },
  {
    selector: ".doctor-slider-one",
    options: {
      loop: true,
      speed: 1000,
      slidesPerView: 1,
      spaceBetween: 24,
      navigation: { nextEl: ".doctor-next", prevEl: ".doctor-prev" },
      breakpoints: {
        0: { slidesPerView: 1 },
        768: { slidesPerView: 2 },
        1200: { slidesPerView: 3 },
        1400: { slidesPerView: 3, spaceBetween: 23 },
        1600: { slidesPerView: 4, spaceBetween: 30 }
      }
    }
  },
  {
    selector: ".brand-slider",
    options: {
      loop: false,
      speed: 8000,
      spaceBetween: 45,
      simulateTouch: false,
      autoplay: { delay: 1, disableOnInteraction: false },
      breakpoints: {
        0: { slidesPerView: 1.8 },
        576: { slidesPerView: 2.8 },
        768: { slidesPerView: 3.5 },
        992: { slidesPerView: 4.5 },
        1200: { slidesPerView: 5.5 },
        1400: { slidesPerView: 6 }
      }
    }
  }
]

export default class extends Controller {
  static initialized = false

  connect() {
    if (this.hasRunAlready()) return
    this.initLazyObservers()
    document.addEventListener("turbo:render", () => this.initLazyObservers())
  }

  hasRunAlready() {
    if (this.constructor.initialized) return true
    this.constructor.initialized = true
    return false
  }

  initLazyObservers() {
    const activeConfigs = sliderConfigs
      .map(cfg => {
        const el = document.querySelector(cfg.selector)
        return el ? { el, config: cfg.options } : null
      })
      .filter(Boolean)

    if (!activeConfigs.length) return

    // IntersectionObserver callback
const observer = new IntersectionObserver((entries, obs) => {
  entries.forEach(async entry => {
    if (!entry.isIntersecting) return
    const el = entry.target
    const className = el.className.split(" ")[0]
    const modulesNeeded = modulesMap[className] || []

    // Dynamically import Swiper core and modules only once
    const importedModule = await import("../vendors/slider.js");
    const SwiperCore = importedModule.default || importedModule;
    const modulesToUse = modulesNeeded.map(mod => importedModule[mod]).filter(Boolean);
    SwiperCore.use(modulesToUse);
    if (!el.swiper) {
      el.swiper = new SwiperCore(el, JSON.parse(el.dataset.swiperConfig));
    }
    obs.unobserve(el);
  })
}, { rootMargin: "200px" })


    activeConfigs.forEach(cfg => {
      cfg.el.dataset.swiperConfig = JSON.stringify(cfg.config)
      observer.observe(cfg.el)
    })
  }
}
